import { useQuery } from '@tanstack/react-query';
import { fetchSavedCampaigns } from '../services/outreach/engagementAnalyticsService';
import { getEmailLogs } from '../services/emailService';
import type { Campaign } from '../types/outreach';
import {
  formatRelativeTime,
  getActivityLevel,
  transformMailerStats,
  transformCampaignStats,
  transformCampaignCardStats,
  type RelativeTimeString,
  type ActivityLevel,
} from '../utils/quickAccessCardTransformers';

/**
 * QuickAccessCardData structure that extends basic card info with activity data
 */
export interface QuickAccessCardData {
  id: string;
  title: string;
  icon: string; // Icon name (e.g., 'email', 'campaign', 'admin_panel_settings', 'person')
  color: string; // Hex color code
  link: string; // Navigation path
  description?: string;
  lastActivity?: RelativeTimeString;
  activityIndicator?: ActivityLevel;
  stats?: Array<{
    label: string;
    value: string | number;
  }>;
}

/**
 * Mailer activity data structure
 * 
 * Represents the latest activity metrics from the mailer system.
 * Used to display last send timestamp and cumulative email count
 * on the Mailer card in the Quick Access dashboard.
 * 
 * @interface MailerActivity
 * @property {string} lastSentAt - ISO 8601 timestamp of last email send (e.g., "2026-03-19T12:00:00Z")
 * @property {number} totalEmailsSent - Cumulative total emails sent through mailer system
 */
interface MailerActivity {
  lastSentAt: string;
  totalEmailsSent: number;
}

/**
 * Fetches actual mailer activity data from SES logs
 * 
 * **Why Real Data is Needed:**
 * - Dashboard must display actual email send activity, not mock data
 * - Users need accurate timestamps to understand mailer performance
 * - Real delivery counts provide insight into email success rates
 * - Enables trend analysis and operational monitoring
 * 
 * Retrieves the most recent email send timestamp and counts successfully
 * delivered emails from SES tracking logs. Provides real metrics for
 * the Mailer card on the Quick Access dashboard.
 * 
 * **Data Fetching:**
 * - Calls `getEmailLogs()` to retrieve all SES tracking records
 * - Filters for successful delivery statuses (delivered, opened, clicked)
 * - Finds most recent activity by timestamp sorting
 * 
 * **Error Handling:**
 * - Catches and logs errors without throwing (prevents UI breakage)
 * - Returns null on error (hook handles graceful degradation)
 * - Component can show "Never" or placeholder when data unavailable
 * 
 * @async
 * @returns {Promise<MailerActivity | null>} Object with lastSentAt and totalEmailsSent, 
 *   or null if no emails exist or fetch fails
 * 
 * @example
 * const activity = await fetchMailerActivity();
 * // => { 
 * //   lastSentAt: "2026-03-19T10:30:00Z",
 * //   totalEmailsSent: 1234
 * // }
 * 
 * @example
 * // If no emails:
 * const activity = await fetchMailerActivity();
 * // => null
 */
async function fetchMailerActivity(): Promise<MailerActivity | null> {
  try {
    /**
     * Fetch real email logs from SES tracking endpoint
     * Returns array of email tracking records with timestamps and status
     */
    const emailLogs = await getEmailLogs();
    
    /**
     * Handle empty result: no emails sent yet
     * Return null to allow component graceful fallback
     */
    if (emailLogs.length === 0) {
      return null;
    }
    
    /**
     * Find most recent email activity
     * 
     * Strategy: Sort by timestamp descending and take first entry
     * This identifies the latest email send for lastActivity display
     * on the Mailer card.
     */
    const sortedByTime = emailLogs.sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
    const mostRecentActivity = sortedByTime[0];
    
    /**
     * Count emails with successful delivery status
     * 
     * **What counts as successful:**
     * - 'delivered': Email successfully delivered to mailbox
     * - 'opened': Email was opened (implies delivery)
     * - 'clicked': Email link was clicked (implies delivery + open)
     * 
     * **Why this definition:**
     * - These statuses indicate successful send completion
     * - Other statuses (bounce, complaint, etc.) indicate delivery failure
     * - Provides true success metrics for mailer performance
     * 
     * **Edge case:** An email may have multiple entries in logs
     * - This counts unique successful delivery events
     * - If email bounced then opened (impossible in practice), counts it
     * - Better to over-count than miss actual successes
     * 
     * @type {number} successfulDeliveries - Count of successful delivery records
     */
    const successfulDeliveries = emailLogs.filter((log) => {
      return (
        log.status === 'delivered' ||
        log.status === 'opened' ||
        log.status === 'clicked'
      );
    }).length;
    
    /**
     * Return aggregated activity metrics
     * 
     * @returns {MailerActivity} Activity data with:
     *   - lastSentAt: ISO timestamp of most recent email
     *   - totalEmailsSent: Count of successful delivery events
     */
    return {
      lastSentAt: mostRecentActivity.timestamp.toISOString(),
      totalEmailsSent: successfulDeliveries,
    };
  } catch (error) {
    /**
     * Error handling: log and return null
     * 
     * Don't throw error - allows hook to gracefully handle failure
     * Component will receive null and show appropriate fallback UI
     * (e.g., "Never" for activity, empty stats)
     * 
     * Possible error causes:
     * - Network error connecting to email service
     * - Authentication failure (user not logged in)
     * - Service temporarily unavailable
     * - Invalid response format from API
     */
    console.error('Error fetching mailer activity from SES logs:', error);
    return null;
  }
}

/**
 * Hook to orchestrate multiple API queries and format data for quick access cards
 * 
 * **Purpose:**
 * Provides a unified interface for fetching and formatting data for the Quick Access
 * dashboard section. Orchestrates parallel queries to Mailer and Campaign APIs,
 * applies real-time transformations, and returns formatted card data.
 * 
 * **Features:**
 * - Parallel query execution (Mailer + Campaigns)
 * - Intelligent caching with 5-minute stale time
 * - Real timestamp formatting (e.g., "2 hours ago")
 * - Activity level indicators (high/medium/low/none)
 * - Automatic retry with exponential backoff
 * - Graceful error handling and fallbacks
 * 
 * **Data Flow:**
 * 1. Initialize two parallel react-query hooks
 * 2. Fetch Mailer activity (real SES logs) and Campaign summaries
 * 3. Transform timestamps to relative time strings
 * 4. Calculate activity level indicators
 * 5. Generate stats arrays for card display
 * 6. Return aggregated data + loading/error states
 * 
 * **Cache Configuration:**
 * - staleTime: 5 minutes (freshness window)
 * - gcTime: 30 minutes (memory retention after unmount)
 * - Disabled: windowFocus and reconnect refetch (prevent excessive requests)
 * - retryDelay: Exponential backoff (1s, 2s, 4s max)
 * 
 * **Why Real Data:**
 * - Dashboard shows actual operational metrics
 * - Real Mailer logs provide true activity timestamps
 * - Campaign data reflects actual outreach performance
 * - Enables users to make informed decisions based on live data
 * 
 * @returns {Object} Hook result object with:
 *   - data: Array of QuickAccessCardData objects ready for rendering
 *   - isLoading: Boolean indicating if any query is in-flight
 *   - error: Error object if any query failed, null otherwise
 *   - refetch: Function to manually refetch all data
 * 
 * @example
 * const { data, isLoading, error, refetch } = useQuickAccessData();
 * 
 * if (isLoading) return <Loading />;
 * if (error) return <Error message={error.message} />;
 * 
 * return (
 *   <div>
 *     {data.map(card => (
 *       <QuickAccessCard key={card.id} {...card} />
 *     ))}
 *   </div>
 * );
 */
export const useQuickAccessData = () => {
  /**
   * QUERY 1: Fetch saved campaigns from outreach service
   * 
   * Retrieves all user campaigns with metadata for Quick Access card display.
   * Used to populate Outreach card with stats about active campaigns.
   * 
   * @type {UseQueryResult<Campaign[], Error>} campaignsQuery
   * - data: Array of Campaign objects with timestamps and metrics
   * - isLoading: True while campaigns fetch is in-flight
   * - error: Error object if campaigns fetch failed
   */
  const campaignsQuery = useQuery<Campaign[], Error>({
    queryKey: ['quick-access-campaigns'],
    queryFn: () => fetchSavedCampaigns(),
    staleTime: 5 * 60 * 1000, // 5 minutes - campaign data doesn't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for quick re-renders
    refetchOnWindowFocus: false, // Disabled to prevent excessive API calls
    refetchOnReconnect: false, // Disabled to prevent excessive API calls
    retry: 2, // Retry failed requests twice
    retryDelay: (attempt) => Math.min(4000, 1000 * attempt), // Exponential backoff: 1s, 2s, 4s
    placeholderData: (previousData) => previousData, // Keep showing old data while refetching
  });

  /**
   * QUERY 2: Fetch mailer activity from SES logs
   * 
   * Retrieves latest email send timestamp and successful delivery count.
   * Used to populate Mailer card with activity metrics.
   * 
   * @type {UseQueryResult<MailerActivity | null, Error>} mailerQuery
   * - data: MailerActivity object or null if no emails sent
   * - isLoading: True while mailer activity fetch is in-flight
   * - error: Error object if mailer fetch failed
   */
  const mailerQuery = useQuery<MailerActivity | null, Error>({
    queryKey: ['quick-access-mailer-activity'],
    queryFn: fetchMailerActivity,
    staleTime: 5 * 60 * 1000, // 5 minutes - good balance of freshness vs requests
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for quick re-renders
    refetchOnWindowFocus: false, // Disabled to prevent excessive API calls
    refetchOnReconnect: false, // Disabled to prevent excessive API calls
    retry: 2, // Retry failed requests twice
    retryDelay: (attempt) => Math.min(4000, 1000 * attempt), // Exponential backoff
  });

  /**
   * TRANSFORMATION: Convert campaign data to displayable stats
   * 
   * Takes raw campaign array and extracts summary metrics
   * (last updated, total campaigns, active campaigns, etc.)
   * 
   * @type {any} campaignStats - Extracted statistics from campaigns
   *   Properties set by transformCampaignStats utility
   */
  const campaignStats = transformCampaignStats(campaignsQuery.data);

  /**
   * CARD GENERATION: Build quick access card data array
   * 
   * Constructs array of 4 QuickAccessCardData objects:
   * 1. Mailer - Email management (with activity timestamp)
   * 2. Outreach - Campaign management (with campaign stats)
   * 3. Admin - Admin panel access (no extra stats)
   * 4. Profile - Profile management (no extra stats)
   * 
   * Each card includes:
   * - id, title, icon, color, link, description (static)
   * - lastActivity, activityIndicator, stats (derived from API data)
   * 
   * @type {QuickAccessCardData[]} quickAccessData
   */
  const quickAccessData: QuickAccessCardData[] = [
    /**
     * MAILER CARD: Email management and activity
     * 
     * Displays:
     * - Last activity: Formatted timestamp of most recent email send (e.g., "2 hours ago")
     * - Activity indicator: Visual indicator of email activity level
     * - Stats: Mailer-specific metrics (if available)
     * 
     * Data source: Real SES logs via fetchMailerActivity()
     * 
     * Fallback behavior when mailerQuery fails:
     * - lastActivity: undefined (component shows "Never")
     * - activityIndicator: "none" (grayed out)
     * - stats: empty array
     */
    {
      id: 'mailer',
      title: 'Mailer',
      icon: 'email',
      color: '#41C8C4', // teal
      link: '/mailer',
      description: 'Manage newsletter and financial emails',
      /**
       * Last activity from most recent email send
       * Formatted as relative time: "2 hours ago", "yesterday", etc.
       * Undefined if no mailer data available
       */
      lastActivity: mailerQuery.data
        ? formatRelativeTime(mailerQuery.data.lastSentAt)
        : undefined,
      /**
       * Activity level indicator for visual styling
       * Computed from time since last send:
       * - high: sent within last hour
       * - medium: sent within last day
       * - low: sent more than 1 day ago
       * - none: never sent or no data
       */
      activityIndicator: getActivityLevel(
        mailerQuery.data?.lastSentAt
      ),
      /**
       * Mailer-specific stats for display
       * Transformation applied to lastSentAt timestamp
       * Returns array of label/value pairs for card display
       */
      stats: transformMailerStats(mailerQuery.data?.lastSentAt),
    },
    
    /**
     * OUTREACH CARD: Campaign management and metrics
     * 
     * Displays:
     * - Last activity: Formatted timestamp of most recent campaign update
     * - Activity indicator: Campaign activity level
     * - Stats: Campaign metrics (count, status, etc.)
     * 
     * Data source: Saved campaigns via fetchSavedCampaigns()
     * 
     * Fallback behavior when campaignsQuery fails:
     * - lastActivity: undefined (component shows "Never")
     * - activityIndicator: "none" (grayed out)
     * - stats: empty array
     */
    {
      id: 'outreach',
      title: 'Outreach',
      icon: 'campaign',
      color: '#2196F3', // blue
      link: '/outreach',
      description: 'Manage outreach campaigns',
      /**
       * Last activity from most recent campaign update
       * Formatted as relative time
       * Undefined if no campaign data available
       */
      lastActivity: campaignStats
        ? formatRelativeTime(campaignStats.lastUpdatedAt)
        : undefined,
      /**
       * Activity level based on campaign update recency
       * Same scale as mailer activity indicator
       */
      activityIndicator: getActivityLevel(
        campaignStats?.lastUpdatedAt
      ),
      /**
       * Campaign-specific stats for card display
       * Transformation applied to aggregated campaign statistics
       * Returns array of label/value pairs
       */
      stats: transformCampaignCardStats(campaignStats),
    },
    
    /**
     * ADMIN CARD: Admin panel access
     * 
     * Static card - no dynamic activity or stats
     * Used for navigation to admin panel
     * 
     * No real-time data needed
     */
    {
      id: 'admin',
      title: 'Admin',
      icon: 'admin_panel_settings',
      color: '#F44336', // red
      link: '/admin',
      description: 'Admin panel access',
      // No additional stats for Admin card
    },
    
    /**
     * PROFILE CARD: User profile management
     * 
     * Static card - no dynamic activity or stats
     * Used for navigation to user profile
     * 
     * No real-time data needed
     */
    {
      id: 'profile',
      title: 'Profile',
      icon: 'person',
      color: '#FF9800', // amber
      link: '/profile',
      description: 'Manage your profile',
      // No additional stats for Profile card
    },
  ];

  /**
   * RETURN OBJECT: Hook result with data, loading state, and error handling
   * 
   * @returns {Object} Hook result containing:
   *   - data: Formatted QuickAccessCardData[] ready for rendering
   *   - isLoading: True if any query (Mailer or Campaigns) is fetching
   *   - error: First error encountered, or null if all queries succeeded
   *   - refetch: Function to manually trigger refetch of all queries
   * 
   * **Loading State:**
   * Combined from both queries - true if either is fetching
   * Prevents partial rendering and ensures consistent UX
   * 
   * **Error State:**
   * Returns first error encountered (prioritizes first query)
   * Component can check and display error message if needed
   * 
   * **Manual Refetch:**
   * Triggers parallel refetch of both queries
   * Useful when user clicks "refresh" button or after data mutations
   */
  return {
    /**
     * Formatted card data ready for rendering
     * Array of 4 QuickAccessCardData objects with all properties set
     */
    data: quickAccessData,
    
    /**
     * Combined loading state from both queries
     * True when either Mailer or Campaigns query is fetching
     * Component should show loading skeleton/spinner when true
     */
    isLoading: mailerQuery.isLoading || campaignsQuery.isLoading,
    
    /**
     * Combined error state from both queries
     * Returns first error encountered (Mailer or Campaigns)
     * Component can check truthiness and display error message
     */
    error: mailerQuery.error || campaignsQuery.error,
    
    /**
     * Manual refetch function
     * 
     * Triggers parallel refetch of both queries:
     * - Mailer activity (SES logs)
     * - Campaigns (Outreach service)
     * 
     * Useful for:
     * - "Refresh" button actions
     * - After mutations (campaign created, email sent, etc.)
     * - Manual user-triggered updates
     * 
     * @returns {Promise<void>} Resolves when all refetches complete
     * 
     * @example
     * const { refetch } = useQuickAccessData();
     * 
     * const handleRefresh = async () => {
     *   await refetch();
     *   // Data will update when queries complete
     * };
     */
    refetch: async () => {
      await Promise.all([mailerQuery.refetch(), campaignsQuery.refetch()]);
    },
  };
};

export type UseQuickAccessDataResult = ReturnType<typeof useQuickAccessData>;
